// useShopTrip — the single state container for a Shop Mode session.
//
// Replaces the 11 useState + 4 ref-mirror + 3 ref-syncing-useEffect
// cascade that lived in ShopMode.jsx. Same pattern as
// experiments/mcm-cooking/additem/store.js: a hand-rolled store with
// useSyncExternalStore selector subscriptions. No Zustand dependency.
//
// Why a single store kills the glitch:
//   - Realtime broadcast posts a single mergeRealtime(payload) action
//     that diffs by id and updates ONLY the affected scan. Subscribers
//     that don't depend on that scan don't re-render.
//   - There's no 4-state cascade per scan (addAnotherPrompt + lastScan
//     + flashVisible + recentScanId all collapse into a single
//     viewState slice, written once per detection).
//   - No ref-mirroring useEffects: actions read from the store snapshot
//     directly, so handleDetected sees fresh state without an effect.
//
// Lifecycle (mirrors the old useShopMode for back-compat):
//   start()                 → creates an 'active' shopping_trips row
//   handleDetected(upc, …)  → lookup + classify + insert/bump trip_scan
//   pairScan(scanId, list)  → bind scan → list item; pair-anchors canonical
//   unpairScan(scanId)
//   armListItem(listItemId) → "next scan goes to this slot"
//   setRedQuickName(scanId, name) → resolves a red row inline
//   adjustQty(scanId, qty)
//   setPhase("scanning"|"resolving"|"checkout")
//   commitTrip({ receipt, insertPantryRow, ... }) → bulk flush
//   cancel()                → marks trip 'cancelled'

import { useSyncExternalStore, useMemo, useEffect } from "react";
import { supabase, safeChannel } from "./supabase";
import { findBarcodeCorrection, rememberBarcodeCorrection } from "./barcodeCorrections";
import { lookupBarcode } from "./lookupBarcode";
import { classifyScan, reclassifyScanRow } from "./classifyScan";
import { stripFlavors } from "./stripFlavors";
import { segmentScanText } from "./segmentScanText";
import { findIngredient, inferCanonicalFromName } from "../data/ingredients";
import { countUnitInfo } from "./countUnitInfo";
import { setActiveStore } from "./useActiveStore";
import { buildCanonicalRoster } from "./canonicalRoster";
import { tilesForIngredient } from "./tileMatching";

function defaultLocationForCategoryLocal(category) {
  if (!category) return null;
  if (["dairy", "meat", "seafood", "produce"].includes(category)) return "fridge";
  return "pantry";
}

// Derive sensible placement defaults for a freshly-inserted scan from
// the canonical's bundled metadata. Centralized so handleDetected and
// pair-anchoring can both write to the placement Map at scan time —
// otherwise green rows that skip the Resolve view land at commit with
// tile_id: null and end up in the misc bucket.
function defaultPlacementForCanonical(canonicalId) {
  if (!canonicalId) return {};
  const ing = findIngredient(canonicalId);
  if (!ing) return {};
  const placement = {};
  const tiles = tilesForIngredient(canonicalId);
  if (tiles.length > 0) placement.tileId = tiles[0];
  const loc = ing.storage?.location || defaultLocationForCategoryLocal(ing.category);
  if (loc) placement.location = loc;
  if (ing.category) placement.category = ing.category;
  return placement;
}

// ── Row shape converters ────────────────────────────────────────────

function tripFromDb(row) {
  return {
    id:        row.id,
    userId:    row.user_id,
    startedAt: row.started_at,
    endedAt:   row.ended_at,
    storeName: row.store_name,
    receiptId: row.receipt_id,
    status:    row.status,
  };
}

function scanFromDb(row) {
  return {
    id:                       row.id,
    tripId:                   row.trip_id,
    userId:                   row.user_id,
    scannedAt:                row.scanned_at,
    barcodeUpc:               row.barcode_upc,
    offPayload:               row.off_payload || null,
    status:                   row.status,                  // 'green'|'yellow'|'red'
    canonicalId:              row.canonical_id,
    brand:                    row.brand,
    productName:              row.product_name,
    qty:                      row.qty ?? 1,
    pairedShoppingListItemId: row.paired_shopping_list_item_id,
    pairedPantryItemId:       row.paired_pantry_item_id,
    pairedReceiptLineIndex:   row.paired_receipt_line_index,
    claims:                   Array.isArray(row.claims) ? row.claims : [],
    placementOverride:        (row.placement_override && typeof row.placement_override === "object")
      ? row.placement_override : null,
  };
}

// Compute the displayName the UI renders — never the bare UPC.
function deriveDisplayName(scan) {
  if (scan.canonicalId) {
    const ing = findIngredient(scan.canonicalId);
    if (ing?.name) return ing.name;
  }
  if (scan.productName?.trim()) return scan.productName.trim();
  return null;
}

// ── Store factory ───────────────────────────────────────────────────

function makeInitialState() {
  return {
    userId:        null,
    isAdmin:       false,          // gates global vs family correction tier
    activeTrip:    null,           // tripFromDb shape | null
    scansById:     new Map(),      // scan.id → scanFromDb shape (incl. displayName)
    scanOrder:     [],             // [scanId] in scan order — preserves UI list
    listTargets:   [],             // shopping_list_items rows (from useShoppingList)
    armedListId:   null,           // "next scan goes here"
    phase:         "scanning",     // 'scanning' | 'resolving' | 'checkout'
    loading:       true,
    // Per-scan placement edits the user makes in Resolve. trip_scans
    // doesn't have columns for location/tile/state/cut/packageSize,
    // so user edits live here and flush onto pantry_items at commit
    // time. Map keyed by scanId.
    placementOverrides: new Map(),

    // Derived arrays — cached per-mutation by setState below so
    // useSyncExternalStore selectors return referentially-stable
    // references. WITHOUT this caching the selectors create a new
    // array on every getSnapshot call, React detects "the store
    // changed" infinitely, and you get
    //   "Maximum update depth exceeded"
    // from forceStoreRerender. Compute once on write, read forever.
    scansArray:      [],
    unresolvedScans: [],

    // viewState collapses the old 4-state flash cascade. One write per
    // scan; the UI reads a single slice.
    viewState: {
      flash: null,                 // { tier, name, until } | null
      lookingUpc: null,            // UPC currently being resolved, or null
      addAnotherPrompt: null,      // { upc, until } | null — same UPC re-scan signal
      countPrompt: null,           // { scanId, canonicalId, label, prefill, until } | null
                                   // — "how many chops?" for count-default scans w/o size
    },
  };
}

// Recompute the derived arrays only when the underlying scan
// collections actually changed. Skipping the recompute for unrelated
// mutations (setListTargets, setPhase, viewState updates) keeps
// scansArray's reference stable so subscribers don't re-render on
// every store write.
function recomputeDerived(prev, next) {
  if (!prev || prev.scansById !== next.scansById || prev.scanOrder !== next.scanOrder) {
    const arr = [];
    for (const id of next.scanOrder) {
      const sc = next.scansById.get(id);
      if (sc) arr.push(sc);
    }
    next.scansArray = arr;
    next.unresolvedScans = arr.filter((sc) => sc.status !== "green");
  } else {
    // Carry the prior derived references through unchanged.
    next.scansArray = prev.scansArray;
    next.unresolvedScans = prev.unresolvedScans;
  }
  return next;
}

function createTripStore() {
  let state = makeInitialState();
  const listeners = new Set();

  function emit() { for (const fn of listeners) fn(); }

  function setState(updater) {
    const next = typeof updater === "function" ? updater(state) : updater;
    if (next === state) return;
    state = recomputeDerived(state, next);
    emit();
  }

  function patchScan(scanId, patch) {
    const cur = state.scansById.get(scanId);
    if (!cur) return;
    const next = { ...cur, ...patch };
    next.displayName = deriveDisplayName(next);
    const nextMap = new Map(state.scansById);
    nextMap.set(scanId, next);
    setState((s) => ({ ...s, scansById: nextMap }));
  }

  function upsertScanRow(scan) {
    const enriched = { ...scan, displayName: deriveDisplayName(scan) };
    const existed = state.scansById.has(enriched.id);
    const nextMap = new Map(state.scansById);
    nextMap.set(enriched.id, enriched);
    // Hydrate the in-memory placementOverrides Map from the row's
    // persisted placement_override JSONB. Without this, a user who
    // closes + reopens the app loses every package size, location,
    // tile, and state they typed because the override Map was
    // ephemeral. Persisted via migration 0170.
    let nextOverrides = state.placementOverrides;
    if (enriched.placementOverride && typeof enriched.placementOverride === "object") {
      nextOverrides = new Map(state.placementOverrides);
      nextOverrides.set(enriched.id, enriched.placementOverride);
    }
    setState((s) => ({
      ...s,
      scansById: nextMap,
      scanOrder: existed ? s.scanOrder : [...s.scanOrder, enriched.id],
      placementOverrides: nextOverrides,
    }));
  }

  function removeScanRow(scanId) {
    if (!state.scansById.has(scanId)) return;
    const nextMap = new Map(state.scansById);
    nextMap.delete(scanId);
    setState((s) => ({
      ...s,
      scansById: nextMap,
      scanOrder: s.scanOrder.filter((id) => id !== scanId),
    }));
  }

  // ── Realtime merge — single-row diff. Never rewrites the array.
  // Subscribers selecting a specific scan (or a derived view) only
  // re-render when their selector output changes.
  function mergeRealtime(payload) {
    if (!payload) return;
    if (payload.eventType === "DELETE") {
      removeScanRow(payload.old?.id);
      return;
    }
    if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
      const incoming = scanFromDb(payload.new);
      upsertScanRow(incoming);
    }
  }

  // ── Trip lifecycle ────────────────────────────────────────────────

  async function refreshActive(userId, isAdmin = false) {
    setState((s) => ({ ...s, userId, isAdmin: !!isAdmin }));
    if (!userId) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    const { data: trips } = await supabase
      .from("shopping_trips")
      .select("*")
      .eq("user_id", userId)
      .eq("status", "active")
      .order("started_at", { ascending: false })
      .limit(1);
    const trip = (trips || [])[0];
    if (!trip) {
      // No live trip → no active store (defensively clears a value
      // orphaned by a crash mid-trip).
      setActiveStore(null);
      setState((s) => ({
        ...s,
        activeTrip: null,
        scansById: new Map(),
        scanOrder: [],
        loading: false,
      }));
      return;
    }
    const { data: tripScans } = await supabase
      .from("trip_scans")
      .select("*")
      .eq("trip_id", trip.id)
      .order("scanned_at", { ascending: true });
    const ordered = (tripScans || []).map(scanFromDb);
    const map = new Map();
    const order = [];
    // Hydrate placementOverrides from the persisted jsonb so user-typed
    // sizes (and other axes) survive reload. Without this, the override
    // Map starts empty after reload, every reclass path calls
    // classifyScan with off?.quantity = null, and rows with custom
    // sizes get demoted from green back to yellow on every mount.
    const overrideMap = new Map();
    for (const s of ordered) {
      const enriched = { ...s, displayName: deriveDisplayName(s) };
      map.set(enriched.id, enriched);
      order.push(enriched.id);
      if (enriched.placementOverride && typeof enriched.placementOverride === "object") {
        overrideMap.set(enriched.id, enriched.placementOverride);
      }
    }
    console.info("[refreshActive.loaded]", {
      tripId: trip.id,
      scanCount: ordered.length,
      scansSummary: ordered.map((s) => ({
        id: s.id,
        canonicalId: s.canonicalId,
        productName: s.productName,
        brand: s.brand,
        status: s.status,
        hasOverride: !!(s.placementOverride && typeof s.placementOverride === "object"),
      })),
    });
    // Resume: re-sync the active-store context from the trip row so
    // house-brand boosting survives an app relaunch mid-trip.
    setActiveStore(trip.store_name || null);
    setState((s) => ({
      ...s,
      activeTrip: tripFromDb(trip),
      scansById: map,
      scanOrder: order,
      placementOverrides: overrideMap,
      loading: false,
    }));
  }

  async function start({ storeName = null } = {}) {
    const userId = state.userId;
    if (!userId) return null;
    const { data, error } = await supabase
      .from("shopping_trips")
      // store_name lands at START, not just from receipt OCR — the
      // "where are you shopping?" pick powers house-brand boosting
      // for the whole trip (useActiveStore) and survives receipt-
      // less commits.
      .insert({ user_id: userId, status: "active", store_name: storeName || null })
      .select("*")
      .single();
    if (error) {
      console.warn("[shop-trip] start failed:", error.message);
      return null;
    }
    const trip = tripFromDb(data);
    setActiveStore(trip.storeName || null);
    setState((s) => ({
      ...s,
      activeTrip: trip,
      scansById: new Map(),
      scanOrder: [],
      phase: "scanning",
    }));
    return trip;
  }

  async function cancel() {
    const tripId = state.activeTrip?.id;
    if (!tripId) return;
    await supabase
      .from("shopping_trips")
      .update({ status: "cancelled", ended_at: new Date().toISOString() })
      // Guard: CheckoutScreen also calls cancel() as LOCAL cleanup
      // after a successful commit — without this predicate that
      // second update clobbered the row's fresh "checked_out"
      // status (and ended_at) back to "cancelled".
      .eq("id", tripId)
      .eq("status", "active");
    setActiveStore(null);
    setState((s) => ({
      ...s,
      activeTrip: null,
      scansById: new Map(),
      scanOrder: [],
      phase: "scanning",
    }));
  }

  // ── Detection — the hot path. Runs lookup + classify + write in one
  //    sweep, then sets viewState.flash. No 4-state cascade.
  async function handleDetected(upc) {
    const userId = state.userId;
    const tripId = state.activeTrip?.id;
    if (!userId || !tripId || !upc) return null;

    const normalized = String(upc || "").trim();

    // Re-scan of an existing UPC bumps qty (matches the unique
    // (trip_id, barcode_upc) constraint on trip_scans).
    const existing = [...state.scansById.values()]
      .find((s) => s.barcodeUpc === normalized);
    if (existing) {
      const nextQty = (existing.qty || 1) + 1;
      const { data, error } = await supabase
        .from("trip_scans")
        .update({ qty: nextQty })
        .eq("id", existing.id)
        .select("*")
        .single();
      if (!error && data) {
        const fresh = scanFromDb(data);
        upsertScanRow(fresh);
        setState((s) => ({
          ...s,
          viewState: {
            ...s.viewState,
            addAnotherPrompt: { upc: normalized, until: Date.now() + 4000 },
            flash: { tier: fresh.status, name: deriveDisplayName(fresh) || "scanned", until: Date.now() + 800 },
            countPrompt: null,   // any new detection clears a pending count prompt
          },
        }));
      }
      return existing;
    }

    setState((s) => ({
      ...s,
      viewState: { ...s.viewState, lookingUpc: normalized },
    }));

    // Parallel resolution: correction tier + OFF lookup. The classifier
    // composes both. Failures don't throw — classifier just gets nulls.
    let correction = null;
    let off = null;
    try {
      [correction, off] = await Promise.all([
        findBarcodeCorrection(normalized).catch(() => null),
        lookupBarcode(normalized).catch(() => null),
      ]);
    } catch (e) {
      console.warn("[shop-trip] resolve cascade failed:", e?.message || e);
    }
    console.info("[handleDetected.lookups]", {
      upc: normalized,
      correction: correction
        ? { canonicalId: correction.canonicalId, brand: correction.brand, productName: correction.productName }
        : null,
      off: off
        ? { found: off.found, productName: off.productName, brand: off.brand, quantity: off.quantity, canonicalId: off.canonicalId }
        : null,
    });

    // Pair-target resolution. Two paths to a pair, in order:
    //
    //   1. Explicit arm: user tapped a list row before scanning. That
    //      tap is the strongest signal of intent — honor it even if
    //      the canonical doesn't match (e.g. they want to pair a
    //      protein bar to a "snack" list slot).
    //
    //   2. Auto-pair on canonical match: if no explicit arm, but the
    //      scanned UPC resolves to a canonical that matches an
    //      unpaired, unpurchased list item's ingredient_id — bind
    //      to it. This is what the user expects: scan nutmeg, list
    //      has nutmeg, the row pairs without ceremony.
    //
    // Pair-anchoring (canonical inheritance from the list item) runs
    // for whichever path picks the target, so a UPC that didn't
    // resolve via OFF/correction can still go GREEN if the list item
    // has a canonical typed in.
    let pairTarget = state.armedListId
      ? state.listTargets.find((t) => t.id === state.armedListId)
      : null;
    let pairSource = pairTarget ? "armed" : null;

    // Find the canonical we'd land on without pair-anchoring so we
    // can match it against list items.
    const preTier = classifyScan({
      upc: normalized,
      correction,
      off,
      armedListItem: null,
    });

    if (!pairTarget && preTier.canonicalId) {
      const pairedIds = new Set();
      for (const sc of state.scansById.values()) {
        if (sc.pairedShoppingListItemId) pairedIds.add(sc.pairedShoppingListItemId);
      }
      pairTarget = (state.listTargets || []).find((t) => {
        if (!t || t.purchasedAt) return false;
        if (pairedIds.has(t.id)) return false;
        const tCanonical = t.ingredientId || t.canonical_id || t.canonicalId || null;
        return tCanonical && tCanonical === preTier.canonicalId;
      }) || null;
      if (pairTarget) pairSource = "auto-canonical";
    }

    // shopping_list_items stores its canonical FK in `ingredient_id`
    // (mapped to `ingredientId` by useShoppingList's fromDb). Treat
    // that as the canonical for pair-anchoring purposes.
    const armedAnchor = pairTarget
      ? {
          canonicalId: pairTarget.ingredientId || pairTarget.canonical_id || pairTarget.canonicalId || null,
          name: pairTarget.name || null,
        }
      : null;

    const tierResult = classifyScan({
      upc: normalized,
      correction,
      off,
      armedListItem: armedAnchor,
    });
    console.info("[handleDetected.classified]", {
      upc: normalized,
      tier: tierResult.tier,
      canonicalId: tierResult.canonicalId,
      brand: tierResult.brand,
      displayName: tierResult.displayName,
      sources: tierResult.sources,
    });

    const insertRow = {
      trip_id:                      tripId,
      user_id:                      userId,
      barcode_upc:                  normalized,
      off_payload:                  off?.found ? off : null,
      status:                       tierResult.tier,
      canonical_id:                 tierResult.canonicalId || null,
      brand:                        tierResult.brand || null,
      product_name:                 tierResult.displayName || null,
      qty:                          1,
      paired_shopping_list_item_id: pairTarget?.id || null,
      // Claims pulled from the OFF productName by stripFlavors —
      // "Spicy Beef Broth" → ["spicy"], "Sharp White Cheddar" →
      // ["sharp","white"], etc. Preserved at scan time so the user
      // sees the variant tag from the start instead of having to
      // re-enter it later. Survives end-to-end into pantry_items.claims.
      claims:                       Array.isArray(tierResult.claims) ? tierResult.claims : [],
    };
    void pairSource; // reserved for telemetry / debug logging

    const { data, error } = await supabase
      .from("trip_scans")
      .insert(insertRow)
      .select("*")
      .single();

    setState((s) => ({
      ...s,
      viewState: { ...s.viewState, lookingUpc: null },
    }));

    if (error || !data) {
      console.warn("[shop-trip] insert failed:", error?.message);
      return null;
    }

    const fresh = scanFromDb(data);
    upsertScanRow(fresh);

    // Autofill placement from canonical metadata so green rows that
    // skip the Resolve view still land in the right tile at commit.
    // Also mirror the OFF-derived package size into the override —
    // GREEN-tier requires a known size, and storing it in placement
    // _override means commitTrip + corrections + future re-scans all
    // read from one canonical place rather than each layer needing
    // the same off_payload-fallback cascade.
    if (fresh.canonicalId) {
      const placement = defaultPlacementForCanonical(fresh.canonicalId);
      const sizeFromOff = tierResult.packageSize;
      const fullPlacement = {
        ...placement,
        ...(sizeFromOff?.amount && sizeFromOff?.unit
          ? { packageAmount: sizeFromOff.amount, packageUnit: sizeFromOff.unit }
          : {}),
      };
      if (Object.keys(fullPlacement).length > 0) {
        // Persist via setPlacementOverride so the placement_override
        // jsonb column stays in lockstep with local state. Synchronous
        // local update happens inside; DB write fires async.
        await setPlacementOverride(fresh.id, fullPlacement);
      }
    }

    // Auto-disarm the list slot once the pairing landed.
    if (state.armedListId) {
      setState((s) => ({ ...s, armedListId: null }));
    }

    // Count-aware prompt — a fresh count-default scan (lamb chops,
    // eggs) with no resolved package size asks "how many chops?" in
    // the aisle instead of silently committing amount=1. Mirrors the
    // addAnotherPrompt mechanics: transient viewState slice with a
    // TTL, cleared by the next detection or by the ScanCapture
    // overlay's confirm/skip. Confirm routes through
    // setPlacementOverride({ packageAmount, packageUnit: "count" }),
    // which reclassifies (yellow→green when brand is present) and
    // flows into pantry_items.amount/max/unit at commit.
    let countPrompt = null;
    const cInfo = countUnitInfo(fresh.canonicalId);
    if (cInfo.isCountDefault) {
      // Size already resolved? Reuse the classifier's own signal —
      // tierResult.packageSize IS classifyScan's hasSize parse of
      // off.quantity (and it's what the auto-fill above mirrored into
      // the placement override). Also consult the correction tier's
      // remembered size: a remembered NON-count size ("1 lb") means
      // this household tracks the product by weight — don't prompt.
      // A remembered COUNT size still prompts (pack counts vary trip
      // to trip) but becomes the stepper prefill for a one-tap confirm.
      const hasOffSize = !!(tierResult.packageSize?.amount && tierResult.packageSize?.unit);
      const corrAmt = Number(correction?.packageSizeAmount);
      const corrHasSize = Number.isFinite(corrAmt) && corrAmt > 0 && !!correction?.packageSizeUnit;
      const corrCountSize = corrHasSize && correction.packageSizeUnit === "count" ? corrAmt : null;
      const corrNonCountSize = corrHasSize && correction.packageSizeUnit !== "count";
      if (!hasOffSize && !corrNonCountSize) {
        countPrompt = {
          scanId: fresh.id,
          canonicalId: fresh.canonicalId,
          label: cInfo.label || "items",
          prefill: corrCountSize || 1,
          until: Date.now() + 8000,
        };
      }
    }

    // Single flash write — covers all three tiers; the UI inspects it.
    setState((s) => ({
      ...s,
      viewState: {
        ...s.viewState,
        flash: {
          tier: fresh.status,
          name: deriveDisplayName(fresh) || normalized,
          until: Date.now() + 800,
        },
        addAnotherPrompt: null,
        countPrompt,
      },
    }));

    return fresh;
  }

  // ── Pair / unpair ─────────────────────────────────────────────────

  async function pairScan(scanId, listItemId) {
    if (!scanId) return;
    // If the list item has a canonical, propagate it to the scan
    // (pair-anchoring rule). The user already declared identity intent
    // by adding "nutmeg" to their list — inherit it.
    const listItem = state.listTargets.find((t) => t.id === listItemId) || null;
    const inherited = {};
    const currentScan = state.scansById.get(scanId);
    if (listItem) {
      const lc = listItem.ingredientId || listItem.canonical_id || listItem.canonicalId || null;
      if (lc && currentScan && !currentScan.canonicalId) {
        inherited.canonical_id = lc;
        const ing = findIngredient(lc);
        if (ing?.name) inherited.product_name = ing.name;
      } else if (!currentScan?.canonicalId && listItem.name) {
        // Try inferring a canonical from the typed list item name.
        const inferred = inferCanonicalFromName(listItem.name);
        if (inferred) {
          inherited.canonical_id = inferred;
          const ing = findIngredient(inferred);
          inherited.product_name = ing?.name || listItem.name;
        } else if (currentScan && !currentScan.productName) {
          // Fall back to the typed name so the row at least shows something.
          inherited.product_name = listItem.name;
        }
      }
    }

    // Compute the correct tier instead of hardcoding green. The
    // green requirement is canonical AND brand AND package size —
    // pair-anchoring alone (canonical only) doesn't satisfy the
    // contract. reclassifyScanRow against the post-update shape
    // gives the right answer for every case (no list pair → red,
    // canonical only → yellow, full triple → green).
    const proposedScan = {
      ...(currentScan || {}),
      canonicalId: inherited.canonical_id ?? currentScan?.canonicalId ?? null,
      productName: inherited.product_name ?? currentScan?.productName ?? null,
      brand: currentScan?.brand ?? null,
      offPayload: currentScan?.offPayload ?? null,
      pairedShoppingListItemId: listItemId || null,
    };
    const armedAnchor = listItem
      ? {
          canonicalId: listItem.ingredientId || listItem.canonical_id || listItem.canonicalId || null,
          name: listItem.name || null,
        }
      : null;
    const reclass = reclassifyScanRow(proposedScan, armedAnchor);
    inherited.status = reclass.tier;

    const update = {
      paired_shopping_list_item_id: listItemId || null,
      ...inherited,
    };
    const { data, error } = await supabase
      .from("trip_scans")
      .update(update)
      .eq("id", scanId)
      .select("*")
      .single();
    if (error || !data) {
      console.warn("[shop-trip] pairScan failed:", error?.message);
      return;
    }
    upsertScanRow(scanFromDb(data));
  }

  async function unpairScan(scanId) {
    if (!scanId) return;
    const { data, error } = await supabase
      .from("trip_scans")
      .update({ paired_shopping_list_item_id: null })
      .eq("id", scanId)
      .select("*")
      .single();
    if (error || !data) return;
    const fresh = scanFromDb(data);
    // Re-tier after unpair. The row may have been green via
    // pair-anchoring (canonical inherited from the list item
    // without OFF or user-supplied data). After unpair, that
    // anchor is gone — the row may need to drop to yellow if
    // canonical now has no brand+size backing.
    const reclass = reclassifyScanRow(fresh, null);
    if (reclass.tier !== fresh.status) {
      const { data: reupdated } = await supabase
        .from("trip_scans")
        .update({ status: reclass.tier })
        .eq("id", scanId)
        .select("*")
        .single();
      if (reupdated) {
        upsertScanRow(scanFromDb(reupdated));
        return;
      }
    }
    upsertScanRow(fresh);
  }

  function armListItem(listItemId) {
    setState((s) => ({ ...s, armedListId: listItemId || null }));
  }

  // ── Red-tier inline name resolution ───────────────────────────────
  // Tap-to-cart for items that have no UPC — fresh produce, bakery,
  // bulk bins. Inserts a synthetic trip_scans row directly paired to
  // the shopping list item, with the canonical inherited from the
  // list. The barcode_upc uses a `manual:` sentinel so the
  // (trip_id, barcode_upc) unique constraint doesn't collide with
  // real UPCs, AND so a second tap of the same list item is a no-op
  // (the unique constraint catches the duplicate insert).
  async function placeListItem(listItem) {
    if (!listItem || !state.tripId || !state.userId) return;
    // No-op if this list item is already paired to a scan in the cart.
    const existing = [...state.scansById.values()].find(
      (s) => s.pairedShoppingListItemId === listItem.id
    );
    if (existing) {
      console.log("[shop-trip] placeListItem: list item already paired, no-op");
      return;
    }
    // Cascade for the canonical:
    //   1. Use whatever canonical is already on the list item.
    //   2. Fall back to inferring from the list item's typed name —
    //      catches "kale", "thyme", and other primitives the user
    //      typed as list text without going through a canonical
    //      picker. Without this fallback, those rows ship YELLOW to
    //      the resolve screen and the user has to bind a canonical
    //      manually before checkout. Awful UX for unambiguous names.
    const inheritedCanonical = listItem.ingredientId
      || listItem.canonical_id
      || listItem.canonicalId
      || (listItem.name ? inferCanonicalFromName(listItem.name) : null)
      || null;
    const sentinelUpc = `manual:${listItem.id}`;
    // Tap-to-add rows have NO scan data — no UPC, no OFF payload, no
    // brand, no package size. Per the tier contract (canonical + brand
    // + size = green), these can NEVER be green at insert time. They
    // ship yellow with canonical bound (or red if even canonical
    // missing); the user fills the rest at the resolve screen and
    // applyScanPatch reclassifies on every edit so they go green
    // when complete.
    const initialStatus = inheritedCanonical ? "yellow" : "red";
    const insertRow = {
      trip_id:                      state.tripId,
      user_id:                      state.userId,
      barcode_upc:                  sentinelUpc,
      off_payload:                  null,
      status:                       initialStatus,
      canonical_id:                 inheritedCanonical,
      brand:                        null,
      product_name:                 inheritedCanonical
                                      ? (findIngredient(inheritedCanonical)?.name || listItem.name || null)
                                      : (listItem.name || null),
      qty:                          1,
      paired_shopping_list_item_id: listItem.id,
    };
    const { data, error } = await supabase
      .from("trip_scans")
      .insert(insertRow)
      .select("*")
      .single();
    if (error || !data) {
      console.warn("[shop-trip] placeListItem insert failed:", error?.message);
      return;
    }
    upsertScanRow(scanFromDb(data));
    // Disarm if this slot was the armed slot — a manual place of
    // the same item supersedes the arm (next scan won't go here).
    if (state.armedListId === listItem.id) {
      setState((s) => ({ ...s, armedListId: null }));
    }
  }

  // Tap-to-place a red scan onto a specific shopping list item. The
  // scan inherits the list item's canonical (when it has one) and
  // pairs to the slot. Faster than typing — one tap and the row
  // resolves. Called from RedQuickNamePrompt when the user picks a
  // tap target instead of typing.
  async function placeRedScan(scanId, listItem) {
    if (!scanId || !listItem) return;
    // Same cascade as placeListItem — list canonical first, then
    // infer from typed list name as a fallback so unambiguous
    // primitives (kale, thyme, etc.) ship GREEN not YELLOW.
    const nameInferred = listItem.name ? inferCanonicalFromName(listItem.name) : null;
    const inheritedCanonical = listItem.ingredientId
      || listItem.canonical_id
      || listItem.canonicalId
      || nameInferred
      || null;
    console.info("[placeRedScan.infer]", {
      scanId,
      listItemId: listItem.id,
      listItemName: listItem.name,
      listItemIngredientId: listItem.ingredientId || null,
      nameInferred,
      inheritedCanonical,
    });
    const proposedName = inheritedCanonical
      ? (findIngredient(inheritedCanonical)?.name || listItem.name || null)
      : (listItem.name || null);
    // Compute the correct tier via reclassifyScanRow against the
    // post-update shape. placeRedScan is the chip-tap path for an
    // existing red SCAN (which has a UPC + OFF payload), so brand
    // and size MAY be present from OFF — green is achievable when
    // they are. The previous dead ternary (`x ? "yellow" : "yellow"`)
    // forced yellow even on rows that should be green.
    const currentScan = state.scansById.get(scanId) || {};
    const proposedScan = {
      ...currentScan,
      canonicalId: inheritedCanonical,
      productName: proposedName,
      brand: currentScan.brand || null,
      offPayload: currentScan.offPayload || null,
      pairedShoppingListItemId: listItem.id,
    };
    const reclass = reclassifyScanRow(proposedScan, {
      canonicalId: listItem.ingredientId || listItem.canonical_id || listItem.canonicalId || null,
      name: listItem.name || null,
    });
    const update = {
      canonical_id: inheritedCanonical || null,
      product_name: proposedName,
      status: reclass.tier,
      paired_shopping_list_item_id: listItem.id,
    };

    const { data, error } = await supabase
      .from("trip_scans")
      .update(update)
      .eq("id", scanId)
      .select("*")
      .single();
    if (error || !data) {
      console.warn("[shop-trip] placeRedScan failed:", error?.message);
      return;
    }
    const refreshed = scanFromDb(data);
    upsertScanRow(refreshed);
    // Teach-back — pair-anchored canonical now belongs to this UPC.
    // The taught NAME follows the two-phase rule: canonical bound →
    // canonical display name (raw productName used to land here and
    // pre-fill marketing copy on every future scan). The segmenter
    // also salvages the variant phrase out of the product text so
    // "Wild Cherry" teaches as a structured variant, not name noise.
    if (refreshed.barcodeUpc && state.userId && refreshed.canonicalId) {
      const teach = {
        userId: state.userId,
        isAdmin: !!state.isAdmin,
        barcodeUpc: refreshed.barcodeUpc,
        canonicalId: refreshed.canonicalId,
      };
      const seg = segmentScanText(refreshed.productName || "", {
        brand: refreshed.brand || null,
        canonicalId: refreshed.canonicalId,
      });
      if (seg.name) teach.name = seg.name;
      if (seg.variant) teach.variant = seg.variant;
      console.info("[placeRedScan.teach]", teach);
      rememberBarcodeCorrection(teach).catch((e) =>
        console.warn("[placeRedScan.teach.failed]", e?.message || e)
      );
    }
    // Disarm if this was the armed slot — same UX contract as the
    // green path. If a different slot was armed, leave it.
    if (state.armedListId === listItem.id) {
      setState((s) => ({ ...s, armedListId: null }));
    }
  }

  async function setRedQuickName(scanId, rawName) {
    const name = (rawName || "").trim();
    if (!scanId || !name) return;
    // Strip flavor / variant tokens per CLAUDE.md OFF-name rules. The
    // user's typed name is intent-to-name, but flavors don't belong in
    // an identity string. Brand still rides separately.
    const stripped = stripFlavors(name, { brand: null, removeFromName: true });
    const cleaned = stripped?.name?.trim() || name;

    // Try to bind a canonical from the typed name. If it resolves,
    // promote tier to green automatically.
    const inferred = inferCanonicalFromName(cleaned);
    console.info("[setRedQuickName.infer]", {
      scanId,
      rawName,
      cleaned,
      inferred,
    });

    // Pair-to-armed-list-item: if the user armed a shopping-list slot
    // before scanning this red UPC (the "tap to place" flow), pair
    // the scan to that slot now. This is what was missing — the user
    // would arm a list item, scan, type the name, hit Save, and the
    // pairing never happened. The handleDetected path does the same
    // pair-target lookup; setRedQuickName needs parity.
    const armedTarget = state.armedListId
      ? (state.listTargets || []).find((t) => t.id === state.armedListId)
      : null;
    // If the list item already has a canonical, inherit it — the user
    // typed "trader joe's chickpea snack" but the list says
    // "chickpeas," let the list canonical win since they explicitly
    // armed that slot. Otherwise infer from the list item's typed
    // name (catches "kale", "thyme" — primitives the user typed in
    // the list without going through a canonical picker). Final
    // fallback: the canonical inferred from what they just typed in
    // the prompt itself.
    const armedListCanonical = armedTarget
      ? (armedTarget.ingredientId || armedTarget.canonical_id || armedTarget.canonicalId || null)
      : null;
    const armedNameInferred = armedTarget?.name
      ? inferCanonicalFromName(armedTarget.name)
      : null;
    const finalCanonical = armedListCanonical || armedNameInferred || inferred || null;

    // Compute the correct tier via reclassifyScanRow rather than
    // hard-coding green. A canonical alone isn't enough — green
    // requires canonical + brand + size per the existing rule. Red
    // scans typically have no brand/size yet, so this lands yellow
    // even when canonical binds; the user fills the rest at the
    // resolve screen.
    const currentScan = state.scansById.get(scanId) || {};
    const proposedName = finalCanonical
      ? (findIngredient(finalCanonical)?.name || cleaned)
      : cleaned;
    const proposedScan = {
      ...currentScan,
      canonicalId: finalCanonical,
      productName: proposedName,
      brand: currentScan.brand || null,
      offPayload: currentScan.offPayload || null,
    };
    const reclass = reclassifyScanRow(proposedScan, armedTarget
      ? { canonicalId: armedTarget.ingredientId || armedTarget.canonical_id || null, name: armedTarget.name || null }
      : null);
    const update = {
      canonical_id: finalCanonical,
      product_name: proposedName,
      status: reclass.tier,
    };

    if (armedTarget) {
      update.paired_shopping_list_item_id = armedTarget.id;
    }

    const { data, error } = await supabase
      .from("trip_scans")
      .update(update)
      .eq("id", scanId)
      .select("*")
      .single();
    if (error || !data) {
      console.warn("[shop-trip] setRedQuickName failed:", error?.message);
      return;
    }
    const refreshed = scanFromDb(data);
    upsertScanRow(refreshed);
    // Teach-back — typed name (and any inferred canonical) belongs to
    // this UPC for next time.
    if (refreshed.barcodeUpc && state.userId) {
      const teach = {
        userId: state.userId,
        isAdmin: !!state.isAdmin,
        barcodeUpc: refreshed.barcodeUpc,
      };
      if (refreshed.canonicalId) teach.canonicalId = refreshed.canonicalId;
      if (refreshed.productName) teach.name = refreshed.productName;
      console.info("[setRedQuickName.teach]", teach);
      rememberBarcodeCorrection(teach).catch((e) =>
        console.warn("[setRedQuickName.teach.failed]", e?.message || e)
      );
    }
    // Disarm after a successful place — same UX contract as the
    // green path. Once the slot is filled, no more pairing for the
    // next scan unless the user arms another row.
    if (armedTarget) {
      setState((s) => ({ ...s, armedListId: null }));
    }
  }

  // One-shot auto-resolve pass — for every yellow scan, try in order:
  //   1. Local inferCanonicalFromName from the typed/scanned product name
  //   2. Haiku via the resolve-scan-canonical edge fn (one call per
  //      unresolved scan, cached globally on success)
  // Idempotent — green rows pass through, no DB churn.
  // Returns the count of rows promoted.
  async function autoResolveYellow() {
    const yellowScans = state.scansArray.filter((s) => s.status === "yellow");
    if (yellowScans.length === 0) return 0;

    // Build the canonical roster ONCE, share across all Haiku calls
    // for this pass — avoids rebuilding it per scan. Guard against
    // any registry-loading hiccup so a roster failure can't take
    // down the resolve screen; we just skip the AI fallback and let
    // local inference handle whatever it can.
    let roster = [];
    try {
      roster = buildCanonicalRoster();
    } catch (e) {
      console.warn("[shop-trip] roster build failed, skipping AI fallback:", e?.message || e);
    }

    let promoted = 0;
    for (const sc of yellowScans) {
      const candidate = sc.productName || sc.displayName || null;
      if (!candidate) continue;

      // Path 1 — local inference (free, deterministic).
      let resolvedId = inferCanonicalFromName(candidate);
      let resolvedName = null;
      let resolvedClaims = null;

      // Path 2 — Haiku fallback. Only when local missed, AND we have
      // enough signal for the AI to work with (a name beyond a couple
      // chars). True red rows with no info skip — they need the photo
      // or wizard pipeline, not text-only inference.
      if (!resolvedId && roster.length > 0 && candidate.trim().length >= 3) {
        try {
          const { data: aiData, error: aiErr } = await supabase.functions.invoke("resolve-scan-canonical", {
            body: {
              upc: sc.barcodeUpc || "",
              product_name: candidate,
              brand: sc.brand || "",
              off_categories: Array.isArray(sc.offPayload?.categoryHints)
                ? sc.offPayload.categoryHints
                : [],
              roster,
            },
          });
          if (!aiErr && aiData && aiData.canonical_id && aiData.confidence !== "low") {
            resolvedId = aiData.canonical_id;
            resolvedName = aiData.name;
            resolvedClaims = aiData.claims;
            console.log("[shop-trip] AI resolved", candidate, "→", aiData.canonical_id, `(${aiData.confidence})`);
          }
        } catch (e) {
          console.warn("[shop-trip] Haiku resolve failed:", e?.message || e);
        }
      }

      if (!resolvedId) continue;

      try {
        const ing = findIngredient(resolvedId);
        const finalName = resolvedName || ing?.name || candidate;
        // Tier comes from reclassifyScanRow against the post-update
        // shape — green only when canonical AND brand AND size are
        // all set. Auto-resolve binds canonical from name; brand and
        // size still need to come from OFF or user input. So the
        // typical outcome here is yellow → yellow with canonical
        // attached, NOT yellow → green. A human-set brand+size at
        // the resolve card flips it green via reclassifyScanRow
        // running on patch.
        const proposedScan = {
          ...sc,
          canonicalId: resolvedId,
          productName: finalName,
        };
        const { tier } = reclassifyScanRow(proposedScan, null);
        const { data, error } = await supabase
          .from("trip_scans")
          .update({
            canonical_id: resolvedId,
            product_name: finalName,
            status: tier,
          })
          .eq("id", sc.id)
          .select("*")
          .single();
        if (error || !data) continue;
        upsertScanRow(scanFromDb(data));
        // resolvedClaims surfaces in console for now — when the
        // claims column lands on trip_scans / pantry_items we'll
        // also persist them here.
        if (resolvedClaims?.length) {
          console.log("[shop-trip] claims for", resolvedId, ":", resolvedClaims);
        }
        promoted += 1;
      } catch (e) {
        console.warn("[shop-trip] autoResolveYellow row failed:", e?.message || e);
      }
    }
    return promoted;
  }

  // Walk every scan in the current trip and re-run reclassifyScanRow
  // against its current shape. Demotes any row that was incorrectly
  // marked green (canonical without brand or size). Idempotent — rows
  // already at the correct tier pass through, no DB churn. Surfaces
  // the existing "phantom green" backlog created when prior code paths
  // hard-coded status: "green" without checking brand/size.
  async function healScanStatuses() {
    let demoted = 0;
    for (const sc of state.scansArray) {
      // Override-aware so user-typed sizes (loose produce, butcher
      // counter, anywhere off.quantity is empty) actually carry weight
      // here. Without this the heal pass demotes any green row whose
      // size came from the user instead of OFF.
      const { tier } = reclassifyWithOverride(sc, null);
      if (tier === sc.status) continue;
      try {
        const { data, error } = await supabase
          .from("trip_scans")
          .update({ status: tier })
          .eq("id", sc.id)
          .select("*")
          .single();
        if (error || !data) continue;
        upsertScanRow(scanFromDb(data));
        if (tier !== "green" && sc.status === "green") demoted += 1;
      } catch (e) {
        console.warn("[shop-trip] healScanStatuses row failed:", e?.message || e);
      }
    }
    return demoted;
  }

  // Reclassify a scan with the user's placement override merged in,
  // so user-typed sizes count toward GREEN. Without this, a user could
  // type "8 oz" on a kale scan, the override would persist, but every
  // OTHER reclass path (healScanStatuses, applyScanPatch on a brand
  // edit) would re-read off.quantity (null) and demote the row back
  // to YELLOW. The classifier itself stays pure — this wrapper is the
  // single owner of "merge user override into the off payload" logic.
  function reclassifyWithOverride(scan, armedListItem = null) {
    const override = state.placementOverrides.get(scan.id) || {};
    const offSyn = scan.offPayload ? { ...scan.offPayload } : {};
    const sizeAmt = override.packageAmount ?? override.max ?? null;
    const sizeUnit = override.packageUnit || override.unit || null;
    if (sizeAmt && sizeUnit) {
      offSyn.quantity = `${sizeAmt} ${sizeUnit}`;
    }
    return classifyScan({
      upc: scan.barcodeUpc,
      correction: {
        canonicalId: scan.canonicalId || null,
        productName: scan.productName || null,
        brand: scan.brand || null,
      },
      off: Object.keys(offSyn).length ? offSyn : null,
      armedListItem,
    });
  }

  async function applyScanPatch(scanId, patch) {
    if (!scanId || !patch) return;
    console.info("[applyScanPatch.start]", { scanId, patch });
    const { data, error } = await supabase
      .from("trip_scans")
      .update(patch)
      .eq("id", scanId)
      .select("*")
      .single();
    if (error || !data) {
      console.warn("[applyScanPatch.dbError]", { scanId, patch, error: error?.message });
      return;
    }
    const fresh = scanFromDb(data);
    console.info("[applyScanPatch.dbWrote]", {
      scanId,
      readback: {
        canonical_id: data.canonical_id,
        product_name: data.product_name,
        brand: data.brand,
        status: data.status,
      },
      freshCanonicalId: fresh.canonicalId,
    });

    // Teach-back: any identity-shaped change to a barcoded scan writes
    // a correction row so the NEXT scan of the same UPC pre-fills with
    // what the user just chose. Fire-and-forget — a correction-write
    // failure must NEVER block the user's edit. Per CLAUDE.md
    // self-teaching rule.
    const touchedIdentity =
      Object.prototype.hasOwnProperty.call(patch, "canonical_id") ||
      Object.prototype.hasOwnProperty.call(patch, "brand") ||
      Object.prototype.hasOwnProperty.call(patch, "product_name");
    if (touchedIdentity && fresh.barcodeUpc && state.userId) {
      const correctionPayload = {
        userId: state.userId,
        isAdmin: !!state.isAdmin,
        barcodeUpc: fresh.barcodeUpc,
      };
      if (fresh.canonicalId) correctionPayload.canonicalId = fresh.canonicalId;
      if (fresh.brand) correctionPayload.brand = fresh.brand;
      if (fresh.productName) {
        if (fresh.canonicalId) {
          // Canonical bound → teach the canonical display name +
          // salvage the variant phrase (two-phase rule; stale OFF
          // text must not ride into the correction tier just because
          // the user edited an adjacent axis).
          const seg = segmentScanText(fresh.productName, {
            brand: fresh.brand || null,
            canonicalId: fresh.canonicalId,
          });
          if (seg.name) correctionPayload.name = seg.name;
          if (seg.variant) correctionPayload.variant = seg.variant;
        } else {
          // Unbound → the resolve screen's name is user-edited (the
          // sanctioned raw-text tier for dead-end UPCs). Verbatim.
          correctionPayload.name = fresh.productName;
        }
      }
      console.info("[applyScanPatch.teach]", correctionPayload);
      rememberBarcodeCorrection(correctionPayload).catch((e) => {
        console.warn("[applyScanPatch.teach.failed]", e?.message || e);
      });
    }
    // Reclassify after edit — tier may flip live (yellow → green when
    // canonical binds, brand types in, etc.). Override-aware so a
    // brand edit on a row that already has a custom-typed size
    // doesn't lose the size at reclass time.
    const armed = state.armedListId
      ? state.listTargets.find((t) => t.id === state.armedListId)
      : null;
    const armedItem = armed
      ? { canonicalId: armed.ingredientId || armed.canonical_id || armed.canonicalId, name: armed.name }
      : null;
    const reclass = reclassifyWithOverride(fresh, armedItem);
    if (reclass.tier !== fresh.status) {
      const reupdated = await supabase
        .from("trip_scans")
        .update({ status: reclass.tier })
        .eq("id", scanId)
        .select("*")
        .single();
      if (!reupdated.error && reupdated.data) {
        upsertScanRow(scanFromDb(reupdated.data));
        return;
      }
    }
    upsertScanRow(fresh);
  }

  async function adjustQty(scanId, nextQty) {
    if (!scanId || !Number.isFinite(nextQty) || nextQty < 1) return;
    const { data } = await supabase
      .from("trip_scans")
      .update({ qty: nextQty })
      .eq("id", scanId)
      .select("*")
      .single();
    if (data) upsertScanRow(scanFromDb(data));
  }

  async function deleteScan(scanId) {
    if (!scanId) return;
    await supabase.from("trip_scans").delete().eq("id", scanId);
    removeScanRow(scanId);
  }

  function setPhase(phase) {
    setState((s) => ({ ...s, phase }));
  }

  function setListTargets(rows) {
    setState((s) => ({ ...s, listTargets: rows || [] }));
  }

  async function setPlacementOverride(scanId, patch) {
    if (!scanId || !patch) return;
    let mergedOverride = null;
    setState((s) => {
      const cur = s.placementOverrides.get(scanId) || {};
      const next = { ...cur };
      for (const [k, v] of Object.entries(patch)) {
        if (v == null) delete next[k];
        else next[k] = v;
      }
      mergedOverride = next;
      const map = new Map(s.placementOverrides);
      if (Object.keys(next).length === 0) map.delete(scanId);
      else map.set(scanId, next);
      return { ...s, placementOverrides: map };
    });

    // Persist the override to trip_scans.placement_override (jsonb)
    // so it survives reload, device handoff, and tab close. This was
    // previously in-memory only — users had to re-enter package
    // size every time they came back to a trip. Migration 0170.
    try {
      const persistShape = mergedOverride && Object.keys(mergedOverride).length > 0
        ? mergedOverride
        : null;
      const { error } = await supabase
        .from("trip_scans")
        .update({ placement_override: persistShape })
        .eq("id", scanId);
      if (error) {
        console.warn("[shop-trip] placement_override persist failed:", error.message);
      }
    } catch (e) {
      console.warn("[shop-trip] placement_override persist threw:", e?.message || e);
    }

    // Reclassify the affected scan with the new override applied so
    // GREEN can land when the user supplies a missing axis (size,
    // location, etc.) via the picker.
    const scan = state.scansById.get(scanId);
    if (!scan) return;
    const override = state.placementOverrides.get(scanId) || {};
    const merged = { ...override, ...patch };
    // Build a synthetic off payload so classifyScan's size check can
    // see the user's typed package size.
    const offSyn = scan.offPayload ? { ...scan.offPayload } : {};
    // Accept both shapes:
    //   - camelCase (packageAmount/packageUnit) from auto-fill writes
    //   - snake_case (max/unit) from the wizard's pantryRow payload
    const sizeAmt = merged.packageAmount ?? merged.max ?? null;
    const sizeUnit = merged.packageUnit || merged.unit || null;
    if (sizeAmt && sizeUnit) {
      offSyn.quantity = `${sizeAmt} ${sizeUnit}`;
    }
    const armed = state.armedListId
      ? state.listTargets.find((t) => t.id === state.armedListId)
      : null;
    const result = classifyScan({
      upc: scan.barcodeUpc,
      correction: {
        canonicalId: scan.canonicalId,
        productName: scan.productName,
        brand: scan.brand,
      },
      off: Object.keys(offSyn).length ? offSyn : null,
      armedListItem: armed
        ? { canonicalId: armed.ingredientId || armed.canonical_id, name: armed.name }
        : null,
    });
    if (result.tier !== scan.status) {
      // Optimistic local update so the UI flips immediately. Then
      // await the DB write — if it fails, ROLL BACK the local state
      // so we never show a tier in the UI that didn't actually
      // persist. Previous fire-and-forget version silently lied to
      // the user when the write errored.
      const prevStatus = scan.status;
      patchScan(scanId, { status: result.tier });
      try {
        const { error } = await supabase
          .from("trip_scans")
          .update({ status: result.tier })
          .eq("id", scanId);
        if (error) {
          console.warn("[shop-trip] tier recompute failed:", error.message);
          patchScan(scanId, { status: prevStatus });   // rollback
        }
      } catch (e) {
        console.warn("[shop-trip] tier recompute threw:", e?.message || e);
        patchScan(scanId, { status: prevStatus });     // rollback
      }
    }
  }

  function clearFlash() {
    setState((s) => s.viewState.flash
      ? { ...s, viewState: { ...s.viewState, flash: null } }
      : s
    );
  }

  // Same shape as clearFlash — the count prompt is a transient
  // viewState slice with a TTL; ScanCapture's timer and its
  // confirm/skip buttons both land here.
  function clearCountPrompt() {
    setState((s) => s.viewState.countPrompt
      ? { ...s, viewState: { ...s.viewState, countPrompt: null } }
      : s
    );
  }

  return {
    getState: () => state,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    refreshActive,
    start,
    cancel,
    handleDetected,
    pairScan,
    unpairScan,
    armListItem,
    setRedQuickName,
    placeRedScan,
    placeListItem,
    autoResolveYellow,
    healScanStatuses,
    applyScanPatch,
    adjustQty,
    deleteScan,
    setPhase,
    setListTargets,
    setPlacementOverride,
    mergeRealtime,
    clearFlash,
    clearCountPrompt,
  };
}

// Module-singleton store. Shop Mode is a single-tenant overlay — only
// one instance is ever mounted at a time, and any caller that wants the
// state subscribes via the hook below. This keeps the realtime
// subscription single-source.
const tripStore = createTripStore();

// useShopTrip — hook with optional selector. Mirrors useDraftStore in
// additem/store.js. Pass a selector to subscribe to a slice; without a
// selector you get the full snapshot (only do this in container
// components — leaf components should always use a selector).
//
//   const phase = useShopTrip(s => s.phase);
//   const trip  = useShopTrip();              // full state (rare)
export function useShopTrip(selector) {
  const sel = selector || ((s) => s);
  // useSyncExternalStore expects a stable getSnapshot that returns
  // referentially-equal values when nothing changed; the underlying
  // setState only emits when state mutates, so the store's reference
  // identity is the right snapshot key.
  const subscribe = useMemo(() => tripStore.subscribe.bind(tripStore), []);
  const getSnapshot = () => sel(tripStore.getState());
  const slice = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return slice;
}

// Action accessors — stable references; safe for use in callbacks.
export const tripActions = {
  refreshActive: (...a) => tripStore.refreshActive(...a),
  // (...a) — start takes { storeName }; a zero-arg wrapper here
  // silently dropped it and made the whole store-context feature
  // inert (caught in adversarial verify).
  start:         (...a) => tripStore.start(...a),
  cancel:        () => tripStore.cancel(),
  handleDetected:(upc) => tripStore.handleDetected(upc),
  pairScan:      (sid, lid) => tripStore.pairScan(sid, lid),
  unpairScan:    (sid) => tripStore.unpairScan(sid),
  armListItem:   (id) => tripStore.armListItem(id),
  setRedQuickName:(sid, name) => tripStore.setRedQuickName(sid, name),
  placeRedScan:  (sid, item) => tripStore.placeRedScan(sid, item),
  placeListItem: (item) => tripStore.placeListItem(item),
  autoResolveYellow: () => tripStore.autoResolveYellow(),
  healScanStatuses:  () => tripStore.healScanStatuses(),
  applyScanPatch:(sid, patch) => tripStore.applyScanPatch(sid, patch),
  adjustQty:     (sid, q) => tripStore.adjustQty(sid, q),
  deleteScan:    (sid) => tripStore.deleteScan(sid),
  setPhase:      (p) => tripStore.setPhase(p),
  setListTargets:(rows) => tripStore.setListTargets(rows),
  setPlacementOverride:(sid, patch) => tripStore.setPlacementOverride(sid, patch),
  clearFlash:    () => tripStore.clearFlash(),
  clearCountPrompt: () => tripStore.clearCountPrompt(),
  rememberOnDemand: (...a) => rememberBarcodeCorrection(...a),
};

// useTripRealtime — owns the trip-scoped realtime channel. Mounts when
// a trip is active; unsubscribes on cleanup. Single-channel, scoped by
// trip_id (per CLAUDE.md egress rules: scoped channels only, never
// global tables).
export function useTripRealtime(activeTripId) {
  useEffect(() => {
    if (!activeTripId) return;
    const channel = safeChannel(`trip-scans-${activeTripId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "trip_scans", filter: `trip_id=eq.${activeTripId}` },
        (payload) => tripStore.mergeRealtime(payload)
      )
      .subscribe();
    return () => { try { channel?.unsubscribe?.(); } catch { /* noop */ } };
  }, [activeTripId]);
}

// Selector helpers — read the cached derived arrays so each call
// returns a referentially-stable reference (only changes when the
// underlying state mutates). Critical for useSyncExternalStore — a
// selector that builds a fresh array on every call triggers an
// infinite re-render loop.

export function selectScansArray(s) {
  return s.scansArray;
}

export function selectUnresolvedScans(s) {
  return s.unresolvedScans;
}

// Tier-specific selector — derives on read, but we accept the
// per-call allocation because callers are rare. If this lands on a
// hot render path, cache it on state like the other two.
export function selectScansByTier(s, tier) {
  return s.scansArray.filter((sc) => sc.status === tier);
}
